import React, { useState, useEffect } from 'react'
import { TextField, Input, InputAdornment } from '@mui/material'
import Button from '../../common/Button'
import Alert from '../../common/Alert'
import useFetch from '../../hooks/useFetch'
import LoadingIndicator from '../../common/LoadingIndicator'
import { urlApi } from '../../config'
import compress from 'compress-base64'

const Influencers = () => {
  const [handle, setHandle] = useState('')
  const [image, setImage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [results, setResults] = useState([])

  const handleSubmit = () => {
    if (!handle || !image) {
      alert('Por favor completa todos los campos')
      return
    }

    setIsSubmitting(true)
  }

  const handleSuccess = data => {
    alert('Influencer creado exitosamente')
    setResults(prev => [
      {
        handle: `@${handle}`,
        image
      },
      ...prev
    ])
    setHandle('')
    setImage('')
    setIsSubmitting(false)
  }

  const handleError = () => {
    alert('Error al crear el influencer')
    setIsSubmitting(false)
  }

  return (
    <div className='flex flex-col items-center bg-white dark:bg-dk-surface h-full overflow-y-auto p-6 gap-6'>
      {/* Create form */}
      <div className='max-w-md bg-white dark:bg-dk-surface rounded-lg shadow-lg p-6 w-full'>
        <p className='text-xl font-bold mb-6 dark:text-gray-100'>Crear Influencer</p>

        {/* Handle */}
        <div className='mb-4'>
          <TextField
            label='Handle'
            variant='outlined'
            fullWidth
            value={handle}
            onChange={e => setHandle(e.target.value)}
            placeholder='nombre_creador'
            InputProps={{
              startAdornment: <InputAdornment position='start'>@</InputAdornment>
            }}
          />
        </div>

        {/* Imagen */}
        <div className='mb-6'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Imagen</label>
          <Input
            style={{ cursor: 'pointer' }}
            name='Imagen'
            placeholder='Selecciona una imagen'
            type='file'
            fullWidth
            onChange={async e => {
              const file = e.target.files[0]
              if (file && file.type.includes('image')) {
                const reader = new FileReader()
                reader.onload = async e => {
                  const compressed = await compress(e.target.result, {
                    width: 400,
                    quality: 0.6,
                    type: 'image/jpeg'
                  })
                  setImage(compressed)
                }
                reader.readAsDataURL(file)
              }
            }}
          />
          {image && (
            <div className='mt-3'>
              <img
                src={image}
                alt='Preview'
                className='w-24 h-24 object-cover rounded-lg border border-gray-200 dark:border-dk-border'
              />
            </div>
          )}
        </div>

        {/* Botón */}
        {isSubmitting ? (
          <div className='w-full flex justify-center py-2'>
            <InfluencerRequest
              body={{
                image,
                handle: `@${handle}`
              }}
              handleSuccess={handleSuccess}
              handleError={handleError}
            />
          </div>
        ) : (
          <Button
            onClick={handleSubmit}
            className='w-full'
            name='Continuar'
            disabled={!handle || !image}
          />
        )}

        {/* Resultados */}
        {results.length > 0 && (
          <>
            {results.map((result, index) => (
              <div key={index} className='mt-6 p-4 bg-green-50 dark:bg-emerald-500/10 border border-green-200 dark:border-dk-border rounded-lg'>
                <h3 className='text-base font-semibold text-green-800 dark:text-emerald-400 mb-3'>Influencer creado</h3>
                <div className='space-y-2 text-sm dark:text-gray-300'>
                  <div>
                    <span className='font-medium'>Handle:</span> {result.handle}
                  </div>
                  {result.image && (
                    <img
                      src={result.image}
                      alt={result.handle}
                      className='w-16 h-16 object-cover rounded-lg mt-2'
                    />
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <InfluencerList />
    </div>
  )
}

const InfluencerList = () => {
  const { loading, data, responseOk } = useFetch(
    `${urlApi}/global/influencers?not_random=true`,
    null
  )
  const [influencers, setInfluencers] = useState([])
  const [deleteHandle, setDeleteHandle] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (responseOk && Array.isArray(data)) {
      setInfluencers(data.filter(influencer => influencer.handle !== 'other'))
    }
  }, [responseOk, data])

  const onDeleted = deletedHandle => {
    setInfluencers(prev => prev.filter(influencer => influencer.handle !== deletedHandle))
  }

  if (loading) {
    return (
      <div className='max-w-md w-full flex justify-center py-8'>
        <LoadingIndicator size={26} />
      </div>
    )
  }

  if (influencers.length === 0) {
    return (
      <div className='max-w-md w-full text-center text-gray-500 dark:text-gray-400 py-8'>
        No hay influencers registrados
      </div>
    )
  }

  return (
    <div className='max-w-md bg-white dark:bg-dk-surface rounded-lg shadow-lg p-6 w-full'>
      <p className='text-xl font-bold mb-4 dark:text-gray-100'>Influencers activos ({influencers.length})</p>
      <div className='space-y-3'>
        {influencers.map(influencer => (
          <div
            key={influencer.handle}
            className='flex items-center gap-3 p-3 border border-gray-200 dark:border-dk-border rounded-lg'
          >
            {influencer.image && (
              <img
                src={influencer.image}
                alt={influencer.handle}
                className='w-12 h-12 object-cover rounded-lg'
              />
            )}
            <div className='flex-1 min-w-0'>
              <p className='font-medium text-sm truncate dark:text-gray-100'>{influencer.handle}</p>
              {influencer.created && (
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  {new Date(influencer.created).toLocaleDateString('es-UY', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </p>
              )}
            </div>
            <button
              className='text-red-500 hover:text-red-700 dark:hover:text-red-400 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors'
              onClick={() => setDeleteHandle(influencer.handle)}
            >
              Eliminar
            </button>
          </div>
        ))}
      </div>

      {/* Delete confirmation modal */}
      {deleteHandle && !isDeleting && (
        <Alert
          visible={true}
          onClose={() => setDeleteHandle(null)}
          onSubmit={() => setIsDeleting(true)}
          onlyConfirm
        >
          <div>
            <p className='font-bold text-lg mb-2 dark:text-gray-100'>Eliminar {deleteHandle}</p>
            <p className='text-gray-600 dark:text-gray-300'>Esto lo removerá de la lista activa de influencers.</p>
          </div>
        </Alert>
      )}

      {/* Delete request */}
      {isDeleting && deleteHandle && (
        <DeleteInfluencerRequest
          handle={deleteHandle}
          onSuccess={() => {
            onDeleted(deleteHandle)
            setDeleteHandle(null)
            setIsDeleting(false)
          }}
          onError={message => {
            alert(message)
            setDeleteHandle(null)
            setIsDeleting(false)
          }}
        />
      )}
    </div>
  )
}

const DeleteInfluencerRequest = ({ handle, onSuccess, onError }) => {
  const { loading, data, responseOk } = useFetch(`${urlApi}/admin/influencers/remove`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ handle })
  })

  useEffect(() => {
    if (!loading) {
      if (responseOk) {
        onSuccess()
      } else if (data?.reason === 'NOT_FOUND') {
        onError('Ya no está en la lista')
      } else {
        onError('No se pudo eliminar')
      }
    }
  }, [loading, responseOk])

  return (
    <Alert visible={true} onClose={() => {}} hideButton>
      <div className='flex items-center gap-2 justify-center'>
        <LoadingIndicator size={20} />
        <span>Eliminando {handle}...</span>
      </div>
    </Alert>
  )
}

const InfluencerRequest = ({ body, handleSuccess, handleError }) => {
  const { loading, data, responseOk } = useFetch(`${urlApi}/admin/influencers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  useEffect(() => {
    if (!loading) {
      if (responseOk) {
        handleSuccess(data)
      } else {
        handleError()
      }
    }
  }, [loading, responseOk])

  return <LoadingIndicator size={26} />
}

export default Influencers
